import { Search, SlidersHorizontal, BookOpen, Languages, Bookmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";

const STEPS = [
  { Icon: Search, title: "Search the catalog", body: "Enter a title, author, subject, or call number. We query Gutendex, Open Library, OpenAlex, and DOAJ in one go." },
  { Icon: SlidersHorizontal, title: "Filter by field, language, region", body: "Use the field chips. Every item shows source, license, and original language." },
  { Icon: BookOpen, title: "Open the free full text", body: "Click Read Free on any card. International books and Western papers open in a new tab at the source." },
  { Icon: Languages, title: "Translate", body: "Coming soon: per-item Translate renders abstracts in your chosen language with a link to the original." },
  { Icon: Bookmark, title: "Save to your shelf", body: "Coming soon: bookmark items to your private My Shelf, exportable in citation formats." },
];

const FEATURED = [
  { author: "Frantz Fanon", work: "The Wretched of the Earth" },
  { author: "W. E. B. Du Bois", work: "The Souls of Black Folk" },
  { author: "Hannah Arendt", work: "The Human Condition" },
  { author: "Achille Mbembe", work: "On the Postcolony" },
  { author: "James Baldwin", work: "Notes of a Native Son" },
  { author: "Simone Weil", work: "The Need for Roots" },
];

export default function LibraryTutorial() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try {
      setDismissed(window.localStorage?.getItem("nsot_tut_dismissed") === "1");
    } catch (e) { /* Safari private mode — treat as not dismissed */ }
  }, []);
  if (!user || dismissed) return null;
  const close = () => {
    try { window.localStorage?.setItem("nsot_tut_dismissed", "1"); } catch (e) {}
    setDismissed(true);
  };

  return (
    <section className="border-b border-[#DCDCDC] bg-[#FAFAFA]" data-testid="library-tutorial">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <div className="eyebrow mb-3">Welcome, Member</div>
            <h2 className="font-headline text-3xl md:text-4xl leading-tight">A short tour of the archive.</h2>
            <p className="font-body text-[#3A3A3A] mt-3 max-w-2xl">Five steps to find international books, Western essays, and community-contributed papers.</p>
          </div>
          <button onClick={close} className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#6C6C6C] hover:text-[#111111]" data-testid="tut-dismiss">Dismiss</button>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-5 border-t border-l border-[#DCDCDC]">
          {STEPS.map((s, i) => (
            <li key={i} className="border-r border-b border-[#DCDCDC] p-6 bg-white">
              <div className="flex items-center justify-between">
                <div className="num text-[#DCDCDC]">{i + 1}</div>
                <s.Icon size={22} strokeWidth={1.25} />
              </div>
              <div className="font-headline text-lg mt-4 leading-tight">{s.title}</div>
              <p className="font-body text-sm text-[#3A3A3A] mt-2">{s.body}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 border-t border-[#DCDCDC] pt-8">
          <div className="eyebrow mb-4">Featured Authors, Sample of the Collection</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
            {FEATURED.map((f) => (
              <div key={f.author}>
                <div className="font-headline text-base leading-tight">{f.author}</div>
                <div className="font-body italic text-sm text-[#6C6C6C] mt-1">{f.work}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
